import type { AppTestCaseGroup } from '../../common/components/ApplicationConfigPage/types'
import { Button, Table, Text } from '../../common/designs'
import { useEnhancedNavigate } from '../../common/hooks'
import ApplicationNameCell from './ApplicationNameCell'
import ApplicationRowStatus from './ApplicationRowStatus'
import { ConfigureDataCollectionSubPathEnum } from './types'
import useManageServerTestCasesAndConfigs from './useManageServerTestCasesAndConfigs'


interface Props {
  applicationList: AppTestCaseGroup[]
}

const ApplicationTable = ({ applicationList }: Props) => {
  const { enhancedNavigate: navigate } = useEnhancedNavigate()
  const { updateRules, isUploadingRules } = useManageServerTestCasesAndConfigs()

  
  const handleToggleApplication = async (application: AppTestCaseGroup) => {
    if (application.hasNoDataCollectionRules || isUploadingRules) return
    const nextTrackingState = !application.isEnabled
    // Collect all rules associated with this application
    const allAppRules = [
      ...application.rules,
    ]
    const updatedRules = allAppRules.map((rule) => {
      return {
        ...rule,
        allow_tracking: nextTrackingState
      }
    })
    await updateRules(updatedRules)
  }

  return (
    <div>
      <div className='mb-3 border-b border-gray-200 pb-3'>
        <Text size='m' weight={600} color='darkGray'>
          Tracked applications
        </Text>
        <Text size='xs' color='lightGray' className='mt-1 max-w-2xl leading-relaxed'>
          Status reflects saved rules and the latest config test report. Open an application to edit
          rules and tests.
        </Text>
      </div>
      <div className='-ml-4'>
        <Table
          key='Applications'
          isDense
          size='s'
          head={['Application', 'Status', 'Configure', 'Enable']}
          body={
            applicationList.map((application) => {
              const isToggleDisabled = application.hasNoDataCollectionRules || isUploadingRules
              return [
                <ApplicationNameCell key={`${application.appName}-name`} application={application} />,
                <ApplicationRowStatus
                  key={`${application.appName}-status`}
                  application={application}
                />,
                <div key={`${application.appName}-actions`}>
                  <Button
                    disabled={!application.isEnabled && !application.hasNoDataCollectionRules}
                    variant='ghost'
                    size='xs'
                    className='text-primary-700 hover:bg-primary-50 hover:text-primary-800'
                    onClick={() => {
                      navigate(
                        `${ConfigureDataCollectionSubPathEnum.APPS}/${encodeURIComponent(
                          application.appName,
                        )}`,
                      )
                    }}
                  >
                    Rules & tests
                  </Button>
                </div>,
                 <div className='flex justify-end' key={`${application.appName}-enabled`}>
                 <label
                   className={`group inline-flex items-center ${
                     isToggleDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                   }`}
                 >
                   <input
                     type='checkbox'
                     className='sr-only'
                     checked={application.isEnabled}
                     disabled={isToggleDisabled}
                     onChange={() => handleToggleApplication(application)}
                   />
                   <span
                     className={`
                       relative inline-block h-5 w-10 transition-colors duration-200 
                       ${application.isEnabled ? 'bg-primary-600' : 'bg-gray-300'}
                       rounded-full
                     `}
                   >
                     <span
                       className={`
                         absolute left-0 top-0 size-5 rounded-full border border-gray-300 bg-white shadow transition-transform
                         duration-200
                         ${application.isEnabled ? 'translate-x-5' : 'translate-x-0'}
                       `}
                     />
                   </span>
                 </label>
               </div>,
              ]
            }) ?? []
          }
        />
      </div>
    </div>
  )
}

export default ApplicationTable
